import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  DeliveryWindow,
  DeliveryWindowForm,
  DeliveryWindowV1Request,
  DeliveryWindowVM
} from '@geofences/delivery-windows/delivery-window';
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DeliveryWindowsService {
  private http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}${environment.apis.ordersEngineAdmin}/v1/delivery-windows`;
  private deliveryWindows = new BehaviorSubject<DeliveryWindow[]>([]);
  deliveryWindows$ = this.deliveryWindows.asObservable();

  getAll(geofenceId?: string, deliveryWindowGuid?: string): Observable<DeliveryWindow[]> {
    // ** geofenceId and deliveryWindowGuid are optional parameters to filter the results **
    const params = {
      ...(geofenceId && { geofenceId }),
      ...(deliveryWindowGuid && { deliveryWindowGuid })
    };
    return this.http.get<ServiceResponseV1<DeliveryWindow[]>>(`${this.apiUrl}`, { params }).pipe(
      map((response) => response.data || []),
      tap((windows) => this.deliveryWindows.next(windows)),
      catchError((error) => this.errorHandler(error, 'Error getting Delivery Windows'))
    );
  }

  getById(deliveryWindowId: string): Observable<ServiceResponseV1<DeliveryWindow>> {
    return this.http
      .get<ServiceResponseV1<DeliveryWindow>>(`${this.apiUrl}/${deliveryWindowId}`)
      .pipe(catchError((error) => this.errorHandler(error, 'Error getting Delivery Window')));
  }

  updateDeliveryWindow(deliveryWindowId: number, dto: DeliveryWindowForm) {
    const patch = this.formatPatchPayload(dto);
    return this.http
      .patch<DeliveryWindowVM>(`${this.apiUrl}/${deliveryWindowId}`, patch)
      .pipe(catchError((error) => this.errorHandler(error, 'Error updating Delivery Window')));
  }

  create(dto: DeliveryWindowV1Request): Observable<ServiceResponseV1<DeliveryWindow>> {
    return this.http
      .post<ServiceResponseV1<DeliveryWindow>>(`${this.apiUrl}`, dto)
      .pipe(catchError((error) => this.errorHandler(error, 'Error creating Delivery Window')));
  }

  delete(id: number): Observable<void> {
    const exists = this.deliveryWindows.value.some((dw) => dw.deliveryWindowId === id);
    if (!exists) {
      return throwError(() => new Error('Delivery window not found'));
    }

    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        this.deliveryWindows.next(this.deliveryWindows.value.filter((dw) => dw.deliveryWindowId !== id));
      }),
      catchError((error) => this.errorHandler(error, 'Error deleting Delivery Window'))
    );
  }

  private errorHandler(error: any, serviceMessage: string) {
    console.error(`[DeliveryWindowsService] ${serviceMessage}, ${error.error.message}`);
    return throwError(() => error);
  }

  private formatPatchPayload(form: DeliveryWindowForm): any[] {
    const patch: any[] = [];

    for (const [key, value] of Object.entries(form)) {
      if (value !== undefined && value !== null) {
        let val: string | number = value;

        // If value is a Date, convert to "HH:mm" string for backend
        if (value instanceof Date) {
          val = this.dateToTimeString(value);
        }

        patch.push({ op: 'replace', path: `/${key}`, value: val });
      }
    }

    return patch;
  }

  private dateToTimeString(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /** Send patch to backend to update delivery window */
}
